import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { platformWorkAPI, taxonomyAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ArrowLeft, Save, Bold, Italic, List, Link2, ImagePlus, Sparkles, Copy, Loader2, FolderKanban,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  not_started: 'bg-gray-100 text-gray-700',
  in_progress: 'bg-blue-100 text-blue-800',
  in_review: 'bg-amber-100 text-amber-800',
  needs_triage: 'bg-red-100 text-red-700',
  completed_reported: 'bg-green-100 text-green-800',
};

export const TaskDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const fileRef = useRef(null);
  const [task, setTask] = useState(null);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingNotes, setSavingNotes] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [kb, setKb] = useState('');
  const [taxonomies, setTaxonomies] = useState({ platform: [], ms_owner: [] });

  const doneValue = stages[stages.length - 1]?.value;
  const isCompleted = task && task.status === doneValue;
  const stageLabel = (v) => stages.find(s => s.value === v)?.label || v || '—';
  const taxLabel = (list, value) => (list || []).find(i => i.value === value)?.label || value || '—';
  const StatusBadge = ({ value }) => (
    <Badge className={STATUS_STYLES[value] || 'bg-gray-100 text-gray-700'}>{stageLabel(value)}</Badge>
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [t, wb, tax] = await Promise.all([
        platformWorkAPI.getById(id),
        platformWorkAPI.getWorkback(),
        taxonomyAPI.getAll(),
      ]);
      setTask(t);
      setStages(wb.stages || []);
      const byId = {};
      (tax || []).forEach(c => { byId[c.id] = c.items || []; });
      setTaxonomies({ platform: byId.platform || [], ms_owner: byId.ms_owner || [] });
      setKb(t.kb_article || '');
      if (editorRef.current) editorRef.current.innerHTML = t.notes_html || '';
    } catch { toast.error('Failed to load task'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!loading && task && editorRef.current && editorRef.current.innerHTML === '') {
      editorRef.current.innerHTML = task.notes_html || '';
    }
  }, [loading, task]);

  const exec = (cmd, val = null) => { document.execCommand(cmd, false, val); editorRef.current?.focus(); };

  // Clean editor HTML before saving: unwrap <p> wrappers execCommand puts around
  // lists (<p><ul>…</ul></p>) and drop empty paragraphs.
  const cleanNotesHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html || '';
    tmp.querySelectorAll('p').forEach(p => {
      if (p.querySelector('ul, ol')) {
        const parent = p.parentNode;
        while (p.firstChild) parent.insertBefore(p.firstChild, p);
        parent.removeChild(p);
      }
    });
    tmp.querySelectorAll('p').forEach(p => {
      if (!p.textContent.trim() && !p.querySelector('img, ul, ol, a')) p.remove();
    });
    return tmp.innerHTML;
  };

  const handleInsertLink = () => {
    const url = window.prompt('Enter URL');
    if (url) exec('createLink', url);
  };

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      toast.loading('Uploading image...', { id: 'imgup' });
      const res = await platformWorkAPI.uploadImage(file);
      const url = `${BACKEND}${res.url}`;
      editorRef.current?.focus();
      document.execCommand('insertHTML', false, `<img src="${url}" style="max-width:100%;border-radius:8px;margin:8px 0;" />`);
      toast.success('Image inserted', { id: 'imgup' });
    } catch { toast.error('Image upload failed', { id: 'imgup' }); }
    finally { e.target.value = ''; }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const cleaned = cleanNotesHtml(editorRef.current?.innerHTML || '');
      if (editorRef.current) editorRef.current.innerHTML = cleaned;
      await platformWorkAPI.update(id, { notes_html: cleaned });
      toast.success('Notes saved');
      setTask(t => ({ ...t, notes_html: cleaned }));
    } catch { toast.error('Failed to save notes'); }
    finally { setSavingNotes(false); }
  };

  const changeStatus = async (value) => {
    setSavingStatus(true);
    try {
      const updated = await platformWorkAPI.update(id, { status: value });
      setTask(t => ({ ...t, status: value, completed_at: updated.completed_at }));
      toast.success('Status updated');
    } catch { toast.error('Failed to update status'); }
    finally { setSavingStatus(false); }
  };

  const generateKb = async () => {
    setGenerating(true);
    try {
      const res = await platformWorkAPI.generateKb(id);
      setKb(res.kb_article);
      toast.success('Knowledge Base article generated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Generation failed'); }
    finally { setGenerating(false); }
  };

  const copyKb = () => {
    const tmp = document.createElement('div'); tmp.innerHTML = kb;
    navigator.clipboard.writeText(tmp.innerText || '');
    toast.success('Article copied to clipboard');
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-lime" /></div>;
  if (!task) return <div className="p-8 text-center text-muted-foreground">Task not found.</div>;

  const proj = task.project;

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in" data-testid="task-detail-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/platform')} data-testid="task-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
        <div className="flex-1">
          <h1 className="text-2xl font-heading font-bold">{task.title}</h1>
          <p className="text-sm text-muted-foreground">Platform (LevelUp++) · Task</p>
        </div>
        <div className="w-56">
          <Select value={task.status || ''} onValueChange={changeStatus} disabled={savingStatus}>
            <SelectTrigger data-testid="task-status-select"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>{stages.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Card className="md:col-span-2">
          <CardHeader><CardTitle className="text-base">Task Info</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-2">
            {task.description && <p className="text-muted-foreground">{task.description}</p>}
            <div className="grid grid-cols-2 gap-2">
              <div><span className="text-muted-foreground">Platform:</span> {taxLabel(taxonomies.platform, task.platform)}</div>
              <div><span className="text-muted-foreground">Target Date:</span> {task.target_date || '—'}</div>
              <div className="flex items-center gap-1.5"><span className="text-muted-foreground">Status:</span> <StatusBadge value={task.status} /></div>
              {task.completed_at && <div><span className="text-muted-foreground">Completed:</span> {task.completed_at.slice(0,10)}</div>}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><FolderKanban className="w-4 h-4" />Project</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-2">
            {proj ? (
              <>
                <button className="text-lime-700 hover:underline font-medium text-left" onClick={() => navigate('/platform')} data-testid="task-project-link">{proj.title}</button>
                <div className="flex items-center gap-1.5 text-xs"><span className="text-muted-foreground">Status:</span> <StatusBadge value={proj.status} /></div>
                <div className="text-xs"><span className="text-muted-foreground">Platform:</span> {taxLabel(taxonomies.platform, proj.platform)}</div>
                <div className="text-xs"><span className="text-muted-foreground">MS Owner:</span> {taxLabel(taxonomies.ms_owner, proj.ms_owner)}</div>
                <div className="text-xs"><span className="text-muted-foreground">Linked PO:</span> {proj.linked_po_number || '—'}</div>
              </>
            ) : <p className="text-muted-foreground text-xs">Standalone task (no project)</p>}
          </CardContent>
        </Card>
      </div>

      {/* Notes editor */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Notes &amp; Screenshots</CardTitle>
          <Button size="sm" onClick={saveNotes} disabled={savingNotes} data-testid="save-notes-btn" className="bg-lime text-lime-foreground hover:bg-lime/90">
            {savingNotes ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}Save Notes
          </Button>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-1 border-b pb-2 mb-2">
            <Button variant="ghost" size="icon" className="h-8 w-8" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('bold')} data-testid="notes-bold"><Bold className="w-4 h-4" /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('italic')}><Italic className="w-4 h-4" /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('insertUnorderedList')} data-testid="notes-bullet-list"><List className="w-4 h-4" /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onMouseDown={(e) => e.preventDefault()} onClick={handleInsertLink}><Link2 className="w-4 h-4" /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onMouseDown={(e) => e.preventDefault()} onClick={() => fileRef.current?.click()} data-testid="notes-insert-image"><ImagePlus className="w-4 h-4" /></Button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImagePick} data-testid="notes-image-input" />
          </div>
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            className="rich-text min-h-[200px] border rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-lime/40 max-w-none"
            data-testid="notes-editor"
          />
        </CardContent>
      </Card>

      {/* AI Knowledge Base builder */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2"><Sparkles className="w-4 h-4 text-lime" />AI Knowledge Base Article</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">Turns this task's objective + notes into a polished KB article for LevelUp admins.</p>
          </div>
          <div className="flex items-center gap-2">
            {kb && <Button size="sm" variant="outline" onClick={copyKb} data-testid="kb-copy-btn"><Copy className="w-4 h-4 mr-1.5" />Copy</Button>}
            <Button size="sm" onClick={generateKb} disabled={!isCompleted || generating} data-testid="generate-kb-btn" className="bg-lime text-lime-foreground hover:bg-lime/90">
              {generating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}{kb ? 'Regenerate' : 'Generate KB Article'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!isCompleted && <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="kb-locked-note">Mark this task as "{stageLabel(doneValue)}" to generate a Knowledge Base article.</p>}
          {kb ? (
            <div className="border rounded-lg p-4 mt-2 prose prose-sm max-w-none" data-testid="kb-article" dangerouslySetInnerHTML={{ __html: kb }} />
          ) : isCompleted ? (
            <p className="text-sm text-muted-foreground">No article yet — click "Generate KB Article".</p>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};

export default TaskDetailPage;
